import type { FC } from "react";

import { InputField } from "@/ui/components/form/Input/InputField";
import { InputError } from "@/ui/components/form/InputError";
import { Label } from "@/ui/components/form/Label";
import { Column } from "@/ui/components/layout/Column";
import { Grid } from "@/ui/components/layout/Grid";
import { Inline } from "@/ui/components/layout/Inline";
import { Button } from "@/ui/components/primitives/Button";
import { cn } from "@/ui/lib/utils";

import type {
  TInlineFormContentProps,
} from "@/ui/components/form/InlineForm/InlineForm.config";

export const InlineFormContent: FC<TInlineFormContentProps> = ({
  label,
  input,
  onAction,
  onKeyDown,
  error,
  button,
  disabled,
  asNestedForm,
  className,
}) => {
  const {
    text: buttonText,
    disabled: isButtonDisabled,
    variant = "tertiary",
    ...buttonProps
  } = button;

  return (
    <Grid gap="3" data-component="InlineFormContent">
      <Column>
        <label className="grid" htmlFor={input.name}>
          <Label className="peer-disabled:opacity-70">
            { label }
          </Label>
        </label>
      </Column>
      <Column>
        <Inline className="w-full">
          <div
            className={cn(
              "cursor-pointer grid gap-x-2 w-full",
              disabled && "cursor-not-allowed",
              className,
            )}
          >
            <InputField
              {...input}
              id={input.name}
              error={error}
              disabled={disabled || input.disabled}
              onKeyDown={
                (e) => {
                  onKeyDown?.();
                  if (e.key === "Enter") {
                    e.preventDefault();
                    onAction?.();
                  }
                }
              }
            />
          </div>
          <Button
            {...buttonProps}
            type={asNestedForm ? "button" : "submit"}
            variant={variant}
            disabled={disabled || isButtonDisabled}
            onClick={onAction}
          >
            { buttonText }
          </Button>
        </Inline>
      </Column>
      { error ? (
        <Column>
          <InputError>{ error }</InputError>
        </Column>
      ) : null }
    </Grid>
  );
};

InlineFormContent.displayName = "InlineFormContent";
